import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FeatureCollection } from 'geojson';
import { Map as MapLibreMap, NavigationControl, setWorkerUrl } from 'maplibre-gl';
import type { GeoJSONSource, MapLayerMouseEvent } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { AirportView, FlightStatusSnapshot, LegView, MacroEvent } from '../../../domain/models/contracts';
import {
  EMPTY_COLLECTION,
  PLANE_ICON_ID,
  buildAirportCollection,
  buildArrivalFeatures,
  buildClosureCollection,
  buildPlaneCollection,
  arrivingFlights,
  buildRouteCollection,
  classifyAirports,
  progressByFlight,
  renderPlaneIcon,
} from './mapGeometry';
import { AIRPORT_MIN_ZOOMS } from './mapGeometry';
import type { AirportMinZoom, AirportTier, ArrivingFlight } from './mapGeometry';
import { BASE_STYLE_URL, MAP_COLORS, TIER_COLORS, TIER_LABELS } from './mapStyle';

/* Vite cannot resolve MapLibre's internal worker path, so point it at the bundled file explicitly. */
// The worker imports maplibre-gl-shared.mjs by a relative path. Vite does not emit that chunk for a ?url worker,
// so both files are served from public/maplibre/ and the worker is loaded from there.
setWorkerUrl(`${import.meta.env.BASE_URL}maplibre/maplibre-gl-worker.mjs`);

const WORLD_CENTRE: [number, number] = [0, 20];
const HOME_ZOOM = 2.6;
const TOOLTIP_OFFSET_PX = 14;
const TIER_ORDER: readonly AirportTier[] = ['HQ', 'HUB', 'NETWORK', 'OTHER'];

const SOURCE = {
  closures: 'closures',
  routes: 'routes',
  airports: 'airports',
  planes: 'planes',
} as const;

const LAYER = {
  closureFill: 'closures-fill',
  closureLine: 'closures-line',
  routesIdle: 'routes-idle',
  routesActive: 'routes-active',
  planes: 'planes',
} as const;

const airportLayerId = (minZoom: AirportMinZoom): string => `airports-z${minZoom}`;
const airportHitLayerId = (minZoom: AirportMinZoom): string => `airports-hit-z${minZoom}`;

export interface WorldMapProps {
  airports: readonly AirportView[];
  legs: readonly LegView[];
  activeFlights: readonly FlightStatusSnapshot[];
  macroEvents: readonly MacroEvent[];
  hqAirportId: string;
  running: boolean;
  speedMultiplier: number;
  tickReceivedAtMs: number;
}

/** Latest animation inputs, read by the frame loop through a ref so the loop never restarts on a tick. */
interface PlaneInput {
  readonly flights: readonly FlightStatusSnapshot[];
  readonly airportsById: ReadonlyMap<string, AirportView>;
  readonly previousProgress: ReadonlyMap<string, number> | null;
  readonly arrivals: readonly ArrivingFlight[];
  readonly running: boolean;
  readonly speedMultiplier: number;
  readonly tickReceivedAtMs: number;
}

/** The flights from the latest tick, and the progress at the tick before it, for easing aircraft between ticks. */
interface TickProgress {
  readonly flights: readonly FlightStatusSnapshot[];
  readonly current: ReadonlyMap<string, number>;
  readonly previous: ReadonlyMap<string, number> | null;
  /** Flights that landed since the previous tick, drawn on their approach until touchdown. */
  readonly arrivals: readonly ArrivingFlight[];
}

interface AirportHover {
  readonly airportId: string;
  readonly x: number;
  readonly y: number;
}

function setSourceData(map: MapLibreMap, sourceId: string, data: FeatureCollection): void {
  const source = map.getSource(sourceId) as GeoJSONSource | undefined;
  void source?.setData(data);
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Adds the empty data sources and the styled layers. Data effects fill them in later. */
function installSceneLayers(map: MapLibreMap): void {
  const planeIcon = renderPlaneIcon(MAP_COLORS.planeFill, MAP_COLORS.planeOutline);
  if (planeIcon !== null) map.addImage(PLANE_ICON_ID, planeIcon);

  map.addSource(SOURCE.closures, { type: 'geojson', data: EMPTY_COLLECTION });
  map.addSource(SOURCE.routes, { type: 'geojson', data: EMPTY_COLLECTION });
  map.addSource(SOURCE.airports, { type: 'geojson', data: EMPTY_COLLECTION });
  map.addSource(SOURCE.planes, { type: 'geojson', data: EMPTY_COLLECTION });

  map.addLayer({
    id: LAYER.closureFill,
    type: 'fill',
    source: SOURCE.closures,
    paint: { 'fill-color': MAP_COLORS.closure, 'fill-opacity': ['*', 0.3, ['get', 'intensity']] },
  });
  map.addLayer({
    id: LAYER.closureLine,
    type: 'line',
    source: SOURCE.closures,
    paint: {
      'line-color': MAP_COLORS.closure,
      'line-width': 1.2,
      'line-opacity': ['*', 0.9, ['get', 'intensity']],
    },
  });
  map.addLayer({
    id: LAYER.routesIdle,
    type: 'line',
    source: SOURCE.routes,
    filter: ['!', ['get', 'active']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': MAP_COLORS.routeIdle, 'line-opacity': 0.55, 'line-width': ['get', 'width'] },
  });
  map.addLayer({
    id: LAYER.routesActive,
    type: 'line',
    source: SOURCE.routes,
    filter: ['get', 'active'],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': MAP_COLORS.routeActive, 'line-opacity': 1, 'line-width': ['get', 'width'] },
  });
  // One layer pair per reveal zoom, so unserved airports only appear once the map is zoomed in far enough.
  for (const minZoom of AIRPORT_MIN_ZOOMS) {
    map.addLayer({
      id: airportLayerId(minZoom),
      type: 'circle',
      source: SOURCE.airports,
      filter: ['==', ['get', 'minZoom'], minZoom],
      minzoom: minZoom,
      paint: {
        // Markers grow as the map zooms in, so the smaller airports stay visible at every scale.
        // zoom may only appear as the input of a top-level interpolate, so the tier radius sits in each output.
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          1,
          ['match', ['get', 'tier'], 'HQ', 7.5, 'HUB', 5.5, 'NETWORK', 4.2, 3.2],
          5,
          ['match', ['get', 'tier'], 'HQ', 13.5, 'HUB', 9.9, 'NETWORK', 7.6, 5.8],
        ],
        'circle-color': [
          'match',
          ['get', 'tier'],
          'HQ',
          TIER_COLORS.HQ,
          'HUB',
          TIER_COLORS.HUB,
          'NETWORK',
          TIER_COLORS.NETWORK,
          TIER_COLORS.OTHER,
        ],
        'circle-stroke-color': MAP_COLORS.ocean,
        'circle-stroke-width': 1.5,
      },
    });
    // Invisible, larger target over each airport so the small markers are easy to hover and click.
    map.addLayer({
      id: airportHitLayerId(minZoom),
      type: 'circle',
      source: SOURCE.airports,
      filter: ['==', ['get', 'minZoom'], minZoom],
      minzoom: minZoom,
      paint: { 'circle-radius': 10, 'circle-opacity': 0, 'circle-stroke-opacity': 0 },
    });
  }
  map.addLayer({
    id: LAYER.planes,
    type: 'symbol',
    source: SOURCE.planes,
    layout: {
      'icon-image': PLANE_ICON_ID,
      'icon-rotate': ['get', 'bearing'],
      'icon-rotation-alignment': 'map',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  });
}

/**
 * Interactive WebGL world map. Airports are coloured by tier, active routes are
 * highlighted, aircraft glide along their great circles between worker ticks,
 * and active airspace closures are drawn as translucent red zones.
 */
export function WorldMap({
  airports,
  legs,
  activeFlights,
  macroEvents,
  hqAirportId,
  running,
  speedMultiplier,
  tickReceivedAtMs,
}: WorldMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const framedRef = useRef(false);
  const planeInputRef = useRef<PlaneInput | null>(null);
  const tickProgressRef = useRef<TickProgress>({
    flights: activeFlights,
    current: progressByFlight(activeFlights),
    previous: null,
    arrivals: [],
  });
  const [loaded, setLoaded] = useState(false);
  const [hover, setHover] = useState<AirportHover | null>(null);

  const airportsById = useMemo(() => new Map(airports.map((a) => [a.id, a] as const)), [airports]);
  const legsById = useMemo(() => new Map(legs.map((l) => [l.id, l] as const)), [legs]);
  const roles = useMemo(() => classifyAirports(airports, legs, hqAirportId), [airports, legs, hqAirportId]);
  const activeLegIds = useMemo(() => new Set(activeFlights.map((f) => f.legId)), [activeFlights]);
  const closures = useMemo(() => buildClosureCollection(macroEvents), [macroEvents]);

  const hqAirport = airportsById.get(hqAirportId);
  const homeLng = hqAirport?.lng;
  const homeLat = hqAirport?.lat;

  if (tickProgressRef.current.flights !== activeFlights) {
    const before = tickProgressRef.current;
    const current = progressByFlight(activeFlights);
    tickProgressRef.current = {
      flights: activeFlights,
      current,
      previous: before.current,
      arrivals: arrivingFlights(before.flights, current, legsById),
    };
  }
  planeInputRef.current = {
    flights: activeFlights,
    airportsById,
    previousProgress: tickProgressRef.current.previous,
    arrivals: tickProgressRef.current.arrivals,
    running,
    speedMultiplier,
    tickReceivedAtMs,
  };

  const drawPlanes = useCallback((): void => {
    const map = mapRef.current;
    const input = planeInputRef.current;
    if (map === null || input === null) return;
    const hourMs = 1000 / Math.max(input.speedMultiplier, 1e-6);
    // Paused: show each aircraft where the latest tick put it, rather than easing from the tick before.
    const hourFraction = input.running ? clamp01((performance.now() - input.tickReceivedAtMs) / hourMs) : 1;
    const airborne = buildPlaneCollection(input.flights, input.airportsById, input.previousProgress, hourFraction);
    const landing = buildArrivalFeatures(input.arrivals, input.airportsById, hourFraction);
    setSourceData(map, SOURCE.planes, { type: 'FeatureCollection', features: [...airborne.features, ...landing] });
  }, []);

  /* Map lifecycle */
  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return undefined;

    const map = new MapLibreMap({
      container,
      style: BASE_STYLE_URL,
      center: WORLD_CENTRE,
      zoom: 1.2,
      minZoom: 1,
      maxZoom: 9,
      maxPitch: 60,
      attributionControl: { compact: true },
    });
    map.addControl(new NavigationControl({ visualizePitch: true }), 'top-left');

    const resizeObserver = new ResizeObserver(() => map.resize());
    resizeObserver.observe(container);

    map.on('load', () => {
      installSceneLayers(map);
      for (const minZoom of AIRPORT_MIN_ZOOMS) {
        map.on('mousemove', airportHitLayerId(minZoom), (event: MapLayerMouseEvent) => {
          const id: unknown = event.features?.[0]?.properties?.['id'];
          if (typeof id !== 'string') return;
          map.getCanvas().style.cursor = 'pointer';
          setHover({ airportId: id, x: event.point.x, y: event.point.y });
        });
        map.on('mouseleave', airportHitLayerId(minZoom), () => {
          map.getCanvas().style.cursor = '';
          setHover(null);
        });
      }
      mapRef.current = map;
      setLoaded(true);
    });

    return () => {
      resizeObserver.disconnect();
      mapRef.current = null;
      framedRef.current = false;
      setLoaded(false);
      map.remove();
    };
  }, []);

  /* Static-ish data: refreshed when the schedule or the network changes */
  useEffect(() => {
    if (!loaded) return;
    const map = mapRef.current;
    if (map === null) return;
    setSourceData(map, SOURCE.routes, buildRouteCollection(legs, airportsById, activeLegIds));
  }, [loaded, legs, airportsById, activeLegIds]);

  useEffect(() => {
    if (!loaded) return;
    const map = mapRef.current;
    if (map === null) return;
    setSourceData(map, SOURCE.airports, buildAirportCollection(airports, roles));
  }, [loaded, airports, roles]);

  useEffect(() => {
    if (!loaded) return;
    const map = mapRef.current;
    if (map === null) return;
    setSourceData(map, SOURCE.closures, closures);
  }, [loaded, closures]);

  /* Aircraft: redraw on every tick, and every animation frame while running */
  useEffect(() => {
    if (loaded) drawPlanes();
  }, [loaded, drawPlanes, activeFlights, legsById, airportsById, running, speedMultiplier, tickReceivedAtMs]);

  useEffect(() => {
    if (!loaded || !running) return undefined;
    let handle = 0;
    const frame = (): void => {
      drawPlanes();
      handle = requestAnimationFrame(frame);
    };
    handle = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(handle);
  }, [loaded, running, drawPlanes]);

  /* Camera: centre on the HQ once, then on demand */
  useEffect(() => {
    const map = mapRef.current;
    if (!loaded || map === null || framedRef.current || homeLng === undefined || homeLat === undefined) return;
    map.jumpTo({ center: [homeLng, homeLat], zoom: HOME_ZOOM });
    framedRef.current = true;
  }, [loaded, homeLng, homeLat]);

  const resetView = (): void => {
    const map = mapRef.current;
    if (map === null) return;
    const centre: [number, number] = homeLng !== undefined && homeLat !== undefined ? [homeLng, homeLat] : WORLD_CENTRE;
    map.easeTo({ center: centre, zoom: HOME_ZOOM, pitch: 0, bearing: 0 });
  };

  const hoveredAirport = hover === null ? undefined : airportsById.get(hover.airportId);
  const hoveredRole = hover === null ? undefined : roles.get(hover.airportId);
  const departures = hover === null ? [] : activeFlights.filter((f) => f.origin === hover.airportId);
  const closureCount = closures.features.length;

  return (
    <div className="map-wrap">
      <div ref={containerRef} className="map-canvas" role="region" aria-label="World route map" />

      <div className="map-tools">
        <button type="button" className="btn btn-icon" onClick={resetView} aria-label="Reset view">
          ⤢
        </button>
      </div>

      {hover !== null && hoveredAirport !== undefined && (
        <div
          className="map-tooltip"
          role="tooltip"
          style={{ left: hover.x + TOOLTIP_OFFSET_PX, top: hover.y + TOOLTIP_OFFSET_PX }}
        >
          <div className="map-tooltip-head">
            <span className="map-tooltip-code">{hoveredAirport.id}</span>
            <span>{hoveredAirport.name}</span>
          </div>
          <div className="muted">
            {hoveredAirport.city}, {hoveredAirport.country}
          </div>
          <div className="map-tooltip-tier" style={{ color: TIER_COLORS[hoveredRole?.tier ?? 'OTHER'] }}>
            {TIER_LABELS[hoveredRole?.tier ?? 'OTHER']}
            {hoveredRole !== undefined && hoveredRole.partnerCount > 0
              ? ` · ${hoveredRole.partnerCount} network partner${hoveredRole.partnerCount === 1 ? '' : 's'}`
              : ''}
          </div>
          <div>Active departures: {departures.length}</div>
          {departures.length > 0 && (
            <ul className="map-tooltip-list">
              {departures.map((f) => (
                <li key={f.aircraftReg}>
                  {f.aircraftReg} → {f.destination} · {f.progressPct.toFixed(0)}%
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="map-legend" aria-label="Map legend">
        {TIER_ORDER.map((tier) => (
          <span key={tier} className="map-legend-item">
            <i className="map-swatch" style={{ background: TIER_COLORS[tier] }} />
            {TIER_LABELS[tier]}
          </span>
        ))}
        {closureCount > 0 && (
          <span className="map-legend-item">
            <i className="map-swatch map-swatch-closure" />
            Airspace closed ({closureCount})
          </span>
        )}
      </div>

      <div className="map-hint">Scroll to zoom · drag to pan · ctrl-drag to tilt</div>
    </div>
  );
}
